import tokens from '@lessonnine/design-tokens.lib/dist/json/variables.json';
import { ShortText } from '../../Typography';
import { Stack } from '../../Layout';
import { font, focusStyles, calcWrapperHeight } from '../../utils';
import { BaseInputProps, TextFieldColor, TextFieldSize, TextFieldState } from './types';

import { styled } from '../../theming';
import { fallbackTheme } from '../../theming/fallbackTheme';
import { Button } from '../../Button';
import {
  stateColors,
  disabledColors,
  readOnlyColors,
  formElementsSharedStyles,
} from '../stylesHelper.styles';

export const sizes = {
  default: {
    containerGap: tokens.space.x3,
    iconSize: tokens.size.x5,
    iconGap: tokens.space.x2,
    iconTop: tokens.space.x3,
    padding: tokens.space.x3,
    prefilledTextPadding: tokens.space.x1,
    prefilledTextWidth: tokens.space.x24,
    fontSize: tokens.typography.shortText.default.fontSize,
    lineHeight: tokens.typography.shortText.default.lineHeight,
  },
  large: {
    containerGap: tokens.space.x4,
    iconSize: tokens.size.x6,
    iconGap: tokens.space.x3,
    iconTop: tokens.space.x4,
    padding: tokens.space.x4,
    prefilledTextPadding: tokens.space.x1,
    prefilledTextWidth: '119px',
    fontSize: tokens.typography.shortText.large.fontSize,
    lineHeight: tokens.typography.shortText.large.lineHeight,
  },
};

const iconSpace = (size: TextFieldSize) =>
  `calc(${sizes[size].iconSize} + ${sizes[size].iconGap} + ${sizes[size].padding})`;

export const BaseInput = styled.input<BaseInputProps>`
  ${({
    theme,
    state,
    fullWidth,
    hasIcon,
    hasActionButton,
    hasPrefilledText,
    color = 'default',
    textFieldSize = 'default',
    disabled,
  }) => {
    const { padding, fontSize, lineHeight } = sizes[textFieldSize];
    const height = calcWrapperHeight(padding, padding, lineHeight);

    const getRightPadding = (
      hasPrefilledText: boolean,
      hasActionButton: boolean,
      textFieldSize: TextFieldSize
    ) => {
      const { padding, prefilledTextWidth, prefilledTextPadding } = sizes[textFieldSize];

      let rightPadding = padding;
      if (hasPrefilledText) {
        rightPadding += ` +  ${prefilledTextPadding} + ${prefilledTextWidth}`;
      }
      if (hasActionButton) {
        rightPadding += ` + ${iconSpace(textFieldSize)}`;
      }
      return `calc(${rightPadding})`;
    };

    return {
      ...formElementsSharedStyles(theme, state, color, padding, height, disabled),
      ...font({
        fontSize,
        lineHeight,
      }),
      width: fullWidth ? '100%' : 'auto',
      paddingRight: getRightPadding(hasPrefilledText, hasActionButton, textFieldSize),
      paddingLeft: hasIcon ? iconSpace(textFieldSize) : padding,
    };
  }}

  /* Disabled the Input Search Icons in Chrome, Firefox, Safari, Edge, Opera */ 
  ::-webkit-search-decoration,
  ::-webkit-search-cancel-button,
  ::-webkit-search-results-button,
  ::-webkit-search-results-decoration {
    display: none;
  }

  /* Disabled the Input Password icons - Microsoft Edge */
  ::-ms-reveal {
    display: none;
  }

  /* Disabled the Input Password icons - Safari */
  ::-webkit-credentials-auto-fill-button {
    visibility: hidden;
  }

  /* Disabled the Input Number arrows in Chrome, Safari, Edge, Opera */
  ::-webkit-outer-spin-button,
  ::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Disabled the Input Number arrows in Firefox */
  -moz-appearance: textfield;
`;

// TODO Currently we use it for actionButton and icon. We should use Button for actionButton
export const IconContainer = styled.div<{
  position?: 'left' | 'right';
  state: TextFieldState;
  color: TextFieldColor;
  size: TextFieldSize;
  disabled?: boolean;
  readOnly?: boolean;
}>`
  ${({ position }) => (position !== 'right' ? 'pointer-events: none;' : '')}
  position: absolute;
  top: ${({ size }) => sizes[size].iconTop};
  ${({ position, size }) =>
    position === 'right' ? `right: ${sizes[size].padding};` : `left: ${sizes[size].padding};`}
  height: ${({ size }) => sizes[size].iconSize};
  width: ${({ size }) => sizes[size].iconSize};
  display: flex;
  align-items: center;
  justify-content: center;
  svg {
    fill: ${({ theme, state, color, disabled, readOnly }) => {
      if (readOnly) return readOnlyColors(theme).color;
      if (disabled) return disabledColors(theme).color;

      return stateColors(theme, state, color).color;
    }};
  }
`;

export const TextFieldButtonBase = styled(Button)`
  ${({ theme }) => focusStyles({ radius: 0, inset: true, theme })}
  color: ${({ theme }) => fallbackTheme(theme).color.surface.foreground.primary};
  svg {
    fill: ${({ theme, disabled }) => {
      if (disabled) return disabledColors(theme).color;
    }};
  }
`;

export const PrefilledTextContainer = styled.div<{
  hasActionButton?: boolean;
  size: TextFieldSize;
  disabled?: boolean;
}>`
  display: inline-block;
  position: absolute;
  top: 0;
  right: 0;
  padding-right: ${({ hasActionButton, size }) =>
    hasActionButton ? iconSpace(size) : sizes[size].padding};
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => fallbackTheme(theme).color.surface.foreground.primary};
`;

export const TextFieldMainContainer = styled(Stack)<{ fullWidth?: boolean }>`
  width: ${({ fullWidth }) => (fullWidth ? '100%' : 'min-content')};
`;

export const TextFieldContainer = styled.div<{ fullWidth?: boolean }>`
  position: relative;
  display: inline-block;
  box-sizing: border-box;
  * {
    box-sizing: inherit;
  }
`;

export const StyledPrefilledText = styled(ShortText)<{
  color: TextFieldColor;
  textFieldSize: TextFieldSize;
  disabled?: boolean;
}>`
  color: ${({ theme, color, disabled }) =>
    disabled
      ? disabledColors(theme).additionalTextColor
      : stateColors(theme, 'default', color).additionalTextColor};
  font-weight: 400;
  padding: 0 ${({ textFieldSize }) => sizes[textFieldSize].prefilledTextPadding};
  font-size: ${({ textFieldSize }) => sizes[textFieldSize].fontSize};
  line-height: ${({ textFieldSize }) => sizes[textFieldSize].lineHeight};
`;

export const StyledLabel = styled('label')<{
  state: TextFieldState;
  color: TextFieldColor;
  size: TextFieldSize;
  disabled?: boolean;
  readOnly?: boolean;
}>`
  font-family: ${tokens.typography.shortText.small.fontFamily};
  font-size: ${({ size }) =>
    tokens.typography.shortText[size === 'default' ? 'small' : 'default'].fontSize};
  color: ${({ theme, state, color, disabled, readOnly }) => {
    if (readOnly) return readOnlyColors(theme).label;
    if (disabled) return disabledColors(theme).label;

    return stateColors(theme, state, color).label;
  }};
  font-weight: 400;
`;
